import { Component } from '@theme/component';
import { ThemeEvents, CartErrorEvent } from '@theme/events';

/**
 * Campos do presenteado para cartão-presente (line item properties nativas Shopify).
 *
 * @typedef {Object} GiftCardRecipientFieldsRefs
 * @property {HTMLInputElement} recipientEmail
 * @property {HTMLInputElement} recipientName
 * @property {HTMLTextAreaElement} recipientMessage
 * @property {HTMLInputElement} recipientSendOn
 * @property {HTMLInputElement} timezoneOffset
 * @property {HTMLSpanElement} [characterCount]
 * @property {HTMLDivElement} [emailError]
 * @property {HTMLDivElement} [nameError]
 * @property {HTMLDivElement} [messageError]
 * @property {HTMLDivElement} [sendOnError]
 * @property {HTMLDivElement} [liveRegion]
 */
class GiftCardRecipientFields extends Component {
  requiredRefs = ['recipientEmail', 'recipientName', 'recipientMessage', 'recipientSendOn', 'timezoneOffset'];

  /** @type {(() => void) | null} */
  #updateCharacterCountBound = null;
  /** @type {((event: Event) => void) | null} */
  #displayCartErrorBound = null;
  /** @type {(() => void) | null} */
  #cartUpdateBound = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setTimezoneOffset();
    this.#setDateConstraints();

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartUpdateBound = () => this.#clearErrorMessages();
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartUpdateBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    if (this.#displayCartErrorBound) {
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartUpdateBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartUpdateBound);
      this.#cartUpdateBound = null;
    }
  }

  #setTimezoneOffset() {
    this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
  }

  #setDateConstraints() {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 90);

    /**
     * @param {Date} date
     * @returns {string}
     */
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    this.refs.recipientSendOn.setAttribute('min', formatDate(today));
    this.refs.recipientSendOn.setAttribute('max', formatDate(maxDate));
  }

  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;
    const template = this.refs.characterCount.getAttribute('data-template');
    if (!template) return;

    this.refs.characterCount.textContent = template
      .replace('[current]', String(currentLength))
      .replace('[max]', String(maxLength));
  }

  /**
   * @param {CartErrorEvent} event
   */
  #displayCartError(event) {
    if (!event.detail?.data) return;

    const { message, errors } = event.detail.data;
    if (errors && typeof errors === 'object') {
      this.#displayErrorMessage(message || '', errors);
    }
  }

  /**
   * @param {string} title
   * @param {Record<string, unknown>} body
   */
  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    /** @type {Record<string, { inputRef: string, errorRef: string }>} */
    const fieldMap = {
      email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
      name: { inputRef: 'recipientName', errorRef: 'nameError' },
      message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
      send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
    };

    for (const [field, errorMessages] of Object.entries(body)) {
      const fieldConfig = fieldMap[field];
      if (!fieldConfig) continue;

      const errorContainer = this.refs[fieldConfig.errorRef];
      const inputElement = this.refs[fieldConfig.inputRef];

      if (errorContainer instanceof HTMLElement) {
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) {
          const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : String(errorMessages);
          errorTextElement.textContent = `${message}.`;
        }
        errorContainer.classList.remove('hidden');
      }

      if (inputElement instanceof HTMLElement) {
        inputElement.setAttribute('aria-invalid', 'true');
        inputElement.setAttribute('aria-describedby', `GiftCardRecipient-${field}-error`);
      }
    }

    if (this.refs.liveRegion && title) {
      this.refs.liveRegion.textContent = title;
    }
  }

  #clearErrorMessages() {
    for (const errorRef of ['emailError', 'nameError', 'messageError', 'sendOnError']) {
      const errorContainer = this.refs[errorRef];
      if (errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) errorTextElement.textContent = '';
      }
    }

    for (const field of [
      this.refs.recipientEmail,
      this.refs.recipientName,
      this.refs.recipientMessage,
      this.refs.recipientSendOn,
    ]) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }
}

customElements.define('gift-card-recipient-fields', GiftCardRecipientFields);
